/**
 * Budget data layer — hooks, month arithmetic, totals, and formatting.
 *
 * The budget is the broad monthly plan (planned vs actual per line),
 * not a transaction ledger — MoneyWiz keeps the day-to-day capture
 * (PROJECT.md §Almanac is a hub). Months are independent snapshots;
 * a whole month saves in one PUT, so the optimistic update is simply
 * "replace this month in the cached list".
 *
 * All arithmetic lives here as pure functions over BudgetSnapshot so
 * the page, the Today tile, and the tests agree on one set of numbers.
 * The one convention to know: annual bills are provisioned at ÷12 into
 * their group — every group subtotal and every headline figure already
 * includes them.
 */

import type {
  BudgetAnnualBill,
  BudgetGroup,
  BudgetItem,
  BudgetMonthDTO,
  BudgetSnapshot,
} from '@almanac/shared';
import { DEFAULT_CURRENCY } from '@almanac/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';
import { mutationKeys, queryKeys } from './queryClient.js';

// ── Hooks ──────────────────────────────────────────────────────────

export function useBudgetMonths() {
  return useQuery<BudgetMonthDTO[]>({
    queryKey: queryKeys.budget,
    queryFn: api.listBudgetMonths,
  });
}

/**
 * Whole-month upsert with an optimistic cache patch: the edited month
 * is swapped into (or inserted into) the cached list immediately, and
 * restored on error. `mutationKey` matches the handler registered in
 * queryClient.ts so a write made offline replays after a reload.
 */
export function useSaveBudgetMonth() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.saveBudgetMonth,
    onMutate: async (vars: { month: string; data: BudgetSnapshot }) => {
      await qc.cancelQueries({ queryKey: queryKeys.budget });
      const previous = qc.getQueryData<BudgetMonthDTO[]>(queryKeys.budget);
      const optimistic: BudgetMonthDTO = {
        month: vars.month,
        data: vars.data,
        updatedAt: new Date().toISOString(),
      };
      qc.setQueryData<BudgetMonthDTO[]>(queryKeys.budget, (old) => {
        const rest = (old ?? []).filter((m) => m.month !== vars.month);
        return [...rest, optimistic].sort((a, b) => a.month.localeCompare(b.month));
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.budget, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.budget });
    },
  });
}

// ── Month keys ─────────────────────────────────────────────────────

/** 'YYYY-MM' for the month containing `now`. */
export function monthKeyFor(now: Date): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** Shift a 'YYYY-MM' key by `delta` months (negative = back). */
export function shiftMonthKey(key: string, delta: number): string {
  const year = Number(key.slice(0, 4));
  const month = Number(key.slice(5, 7)) - 1 + delta;
  const d = new Date(Date.UTC(year, month, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** 'February 2026' for the month switcher and sheets. */
export function monthLabel(key: string): string {
  const d = new Date(`${key}-01T00:00:00`);
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

/** Just the month name — 'February' — for rail headings. */
export function monthName(key: string): string {
  const d = new Date(`${key}-01T00:00:00`);
  return d.toLocaleDateString(undefined, { month: 'long' });
}

// ── Formatting ─────────────────────────────────────────────────────

/**
 * Amounts render as rounded whole numbers — this is broad-level
 * budgeting, and the ÷12 provisions would otherwise drag .33s and
 * .67s across every card. The stored values keep their precision.
 */
export function fmtAmount(value: number): string {
  // Comma grouping, as the approved mockups showed (the old tracker's
  // Swiss 10'800 style read as a typo outside a CHF-only context).
  return Math.round(value).toLocaleString('en-GB');
}

/** 'CHF 10,800' — headline figures where the unit matters. */
export function fmtMoney(value: number, currency?: string | null): string {
  return `${currency ?? DEFAULT_CURRENCY} ${fmtAmount(value)}`;
}

/**
 * Diff column: '+15' over, '−15' under, '–' for on-plan. The sign is
 * spend-relative (actual − planned) for expenses; income rows flip it
 * at the call site so "more income than planned" reads as good.
 */
export function fmtDiff(diff: number): string {
  const rounded = Math.round(diff);
  if (rounded === 0) return '–';
  return rounded > 0 ? `+${fmtAmount(rounded)}` : `−${fmtAmount(Math.abs(rounded))}`;
}

// ── Arithmetic ─────────────────────────────────────────────────────

/** Monthly provision for one bill. */
export function billMonthly(bill: BudgetAnnualBill): number {
  return bill.annualAmount / 12;
}

/** The bills provisioned into a given group. */
export function billsForGroup(snapshot: BudgetSnapshot, groupId: string): BudgetAnnualBill[] {
  return snapshot.annualBills.filter((b) => b.groupId === groupId);
}

/**
 * One group's subtotal, provisions included. Provisions count into
 * both columns — a provisioned bill is committed money whether or not
 * it was paid this month.
 */
export function groupTotals(
  snapshot: BudgetSnapshot,
  group: BudgetGroup,
): { planned: number; actual: number } {
  let planned = 0;
  let actual = 0;
  for (const item of group.items) {
    planned += item.planned;
    actual += item.actual;
  }
  for (const bill of billsForGroup(snapshot, group.id)) {
    const monthly = billMonthly(bill);
    planned += monthly;
    actual += monthly;
  }
  return { planned, actual };
}

export interface BudgetTotals {
  incomePlanned: number;
  incomeActual: number;
  /** Expenses + savings + provisions: everything the plan spends. */
  committedPlanned: number;
  committedActual: number;
  savingsPlanned: number;
  savingsActual: number;
  /** Sum of all annual-bill ÷12 provisions. */
  provisionsMonthly: number;
  balancePlanned: number;
  balanceActual: number;
  /** Planned savings as a share of planned income; 0 when no income. */
  savingsRate: number;
}

export function computeTotals(snapshot: BudgetSnapshot): BudgetTotals {
  let incomePlanned = 0;
  let incomeActual = 0;
  let committedPlanned = 0;
  let committedActual = 0;
  let savingsPlanned = 0;
  let savingsActual = 0;

  for (const group of snapshot.groups) {
    const t = groupTotals(snapshot, group);
    if (group.kind === 'income') {
      incomePlanned += t.planned;
      incomeActual += t.actual;
    } else {
      committedPlanned += t.planned;
      committedActual += t.actual;
      if (group.kind === 'savings') {
        savingsPlanned += t.planned;
        savingsActual += t.actual;
      }
    }
  }

  const provisionsMonthly = snapshot.annualBills.reduce((sum, b) => sum + billMonthly(b), 0);

  return {
    incomePlanned,
    incomeActual,
    committedPlanned,
    committedActual,
    savingsPlanned,
    savingsActual,
    provisionsMonthly,
    balancePlanned: incomePlanned - committedPlanned,
    balanceActual: incomeActual - committedActual,
    savingsRate: incomePlanned > 0 ? savingsPlanned / incomePlanned : 0,
  };
}

/**
 * Where the month is actually heading, given what's been entered so
 * far: every line contributes max(planned, actual). An overrun sticks
 * (money spent can't be unspent; income received is real), but an
 * underrun isn't banked until the month ends — most actuals are blank
 * mid-month, and a naive actual balance would read absurdly positive.
 * ÷12 provisions count at their monthly figure as always. Equals the
 * planned balance while nothing diverges.
 */
export function projectedBalance(snapshot: BudgetSnapshot): number {
  let income = 0;
  let committed = 0;
  for (const group of snapshot.groups) {
    let sum = 0;
    for (const item of group.items) sum += Math.max(item.planned, item.actual);
    for (const bill of billsForGroup(snapshot, group.id)) sum += billMonthly(bill);
    if (group.kind === 'income') income += sum;
    else committed += sum;
  }
  return income - committed;
}

/**
 * Carry a month forward: the plan (and the bills) travel, the actuals
 * start blank — a fresh month begins with intentions, not last month's
 * spending. (The old tracker meant to do this and didn't; fixed here.)
 */
export function carryForward(from: BudgetSnapshot): BudgetSnapshot {
  return {
    groups: from.groups.map((g) => ({
      ...g,
      items: g.items.map((i) => ({ ...i, actual: 0 })),
    })),
    annualBills: from.annualBills.map((b) => ({ ...b })),
  };
}

/**
 * Status line for the Budget tile on Today: the current month's
 * planned balance, or a nudge to set the month up. Undefined while
 * the list is still loading (the tile shows its default).
 */
export function budgetTileSummary(
  months: BudgetMonthDTO[] | undefined,
  now: Date,
): string | undefined {
  if (!months) return undefined;
  const m = months.find((x) => x.month === monthKeyFor(now));
  if (!m) return 'Plan the month';
  const t = computeTotals(m.data);
  // Once reality diverges from the plan, the tile tells the truth the
  // banner's projection line tells: where the month is heading.
  const projected = projectedBalance(m.data);
  if (Math.round(projected) !== Math.round(t.balancePlanned)) {
    return `Heading ${projected >= 0 ? '+' : '−'}${fmtAmount(Math.abs(projected))}`;
  }
  return `Balance ${t.balancePlanned >= 0 ? '+' : '−'}${fmtAmount(Math.abs(t.balancePlanned))}`;
}

// ── Snapshot edits ─────────────────────────────────────────────────
// Every edit is copy-on-write over the whole snapshot: the page builds
// the next state with one of these and PUTs it as the new month.

export function updateBudgetItem(
  s: BudgetSnapshot,
  groupId: string,
  itemId: string,
  patch: Partial<Pick<BudgetItem, 'label' | 'planned' | 'actual'>>,
): BudgetSnapshot {
  return {
    ...s,
    groups: s.groups.map((g) =>
      g.id === groupId
        ? { ...g, items: g.items.map((i) => (i.id === itemId ? { ...i, ...patch } : i)) }
        : g,
    ),
  };
}

export function addBudgetItem(s: BudgetSnapshot, groupId: string, item: BudgetItem): BudgetSnapshot {
  return {
    ...s,
    groups: s.groups.map((g) => (g.id === groupId ? { ...g, items: [...g.items, item] } : g)),
  };
}

export function removeBudgetItem(s: BudgetSnapshot, groupId: string, itemId: string): BudgetSnapshot {
  return {
    ...s,
    groups: s.groups.map((g) =>
      g.id === groupId ? { ...g, items: g.items.filter((i) => i.id !== itemId) } : g,
    ),
  };
}

export function updateBudgetGroup(
  s: BudgetSnapshot,
  groupId: string,
  patch: Partial<Pick<BudgetGroup, 'title' | 'kind' | 'fixed'>>,
): BudgetSnapshot {
  return {
    ...s,
    groups: s.groups.map((g) => (g.id === groupId ? { ...g, ...patch } : g)),
  };
}

export function addBudgetGroup(s: BudgetSnapshot, group: BudgetGroup): BudgetSnapshot {
  return { ...s, groups: [...s.groups, group] };
}

/** Removing a group also removes the annual bills provisioned into it —
 *  a provision with no destination would silently vanish from view
 *  while still inflating the headline totals. */
export function removeBudgetGroup(s: BudgetSnapshot, groupId: string): BudgetSnapshot {
  return {
    groups: s.groups.filter((g) => g.id !== groupId),
    annualBills: s.annualBills.filter((b) => b.groupId !== groupId),
  };
}

/**
 * Set a group's line order from an id list (the group sheet stages its
 * order locally and applies it on Save). Ids are resolved against the
 * group's *current* items, so a line renamed or revalued meanwhile
 * keeps its latest figures; any item missing from the list (added
 * elsewhere mid-edit) is appended rather than dropped.
 */
export function reorderBudgetItems(
  s: BudgetSnapshot,
  groupId: string,
  orderedIds: string[],
): BudgetSnapshot {
  return {
    ...s,
    groups: s.groups.map((g) => {
      if (g.id !== groupId) return g;
      const byId = new Map(g.items.map((i) => [i.id, i]));
      const ordered = orderedIds
        .map((id) => byId.get(id))
        .filter((i): i is BudgetItem => Boolean(i));
      const leftover = g.items.filter((i) => !orderedIds.includes(i.id));
      return { ...g, items: [...ordered, ...leftover] };
    }),
  };
}

/**
 * Move a line to another group, figures intact, appended at the end
 * (its new neighbours' order is the target group's business). No-op
 * when source and target match or anything is missing.
 */
export function moveBudgetItemToGroup(
  s: BudgetSnapshot,
  fromGroupId: string,
  itemId: string,
  toGroupId: string,
): BudgetSnapshot {
  if (fromGroupId === toGroupId) return s;
  const item = s.groups.find((g) => g.id === fromGroupId)?.items.find((i) => i.id === itemId);
  if (!item || !s.groups.some((g) => g.id === toGroupId)) return s;
  return {
    ...s,
    groups: s.groups.map((g) => {
      if (g.id === fromGroupId) return { ...g, items: g.items.filter((i) => i.id !== itemId) };
      if (g.id === toGroupId) return { ...g, items: [...g.items, item] };
      return g;
    }),
  };
}

/** Move a group to a given position on the page (clamped). */
export function moveBudgetGroupToIndex(
  s: BudgetSnapshot,
  groupId: string,
  index: number,
): BudgetSnapshot {
  const from = s.groups.findIndex((g) => g.id === groupId);
  if (from < 0) return s;
  const to = Math.max(0, Math.min(s.groups.length - 1, index));
  if (to === from) return s;
  const groups = [...s.groups];
  const [group] = groups.splice(from, 1);
  groups.splice(to, 0, group!);
  return { ...s, groups };
}

export function upsertBudgetBill(s: BudgetSnapshot, bill: BudgetAnnualBill): BudgetSnapshot {
  const exists = s.annualBills.some((b) => b.id === bill.id);
  return {
    ...s,
    annualBills: exists
      ? s.annualBills.map((b) => (b.id === bill.id ? bill : b))
      : [...s.annualBills, bill],
  };
}

export function removeBudgetBill(s: BudgetSnapshot, billId: string): BudgetSnapshot {
  return { ...s, annualBills: s.annualBills.filter((b) => b.id !== billId) };
}

// ── Starter template ───────────────────────────────────────────────

function makeGroup(
  id: string,
  title: string,
  kind: BudgetGroup['kind'],
  items: [string, string, number?][],
): BudgetGroup {
  return {
    id,
    title,
    kind,
    items: items.map(([itemId, label, planned]) => ({
      id: itemId,
      label,
      planned: planned ?? 0,
      actual: 0,
    })),
  };
}

/**
 * A new user's first month: the group and line vocabulary from the
 * budget tracker this domain grew out of, with every figure at zero.
 * Groups and items are fully editable from there — this is a starting
 * vocabulary, not a fixed chart of accounts.
 */
export function starterSnapshot(): BudgetSnapshot {
  return {
    groups: [
      makeGroup('income', 'Income', 'income', [
        ['primarySalary', 'Primary Salary'],
        ['extraIncome', 'Extra Income'],
      ]),
      makeGroup('housing', 'Housing', 'expense', [
        ['rent', 'Rent'],
        ['mobilePhone', 'Mobile Phone'],
        ['electricity', 'Electricity'],
        ['internetTv', 'Internet & TV'],
        ['maintenance', 'Maintenance & Repairs'],
      ]),
      makeGroup('transport', 'Transportation', 'expense', [
        ['publicTransport', 'Public Transport'],
        ['fuel', 'Fuel'],
        ['carMaintenance', 'Maintenance'],
      ]),
      makeGroup('food', 'Food & Dining', 'expense', [
        ['groceries', 'Groceries'],
        ['lunches', 'Lunches & Cafes'],
        ['diningOut', 'Dining Out'],
      ]),
      makeGroup('insurance', 'Insurance', 'expense', [
        ['insHealth', 'Health'],
        ['insLife', 'Life'],
      ]),
      makeGroup('personalCare', 'Personal Care', 'expense', [
        ['medical', 'Medical'],
        ['clothing', 'Clothing & Accessories'],
        ['gym', 'Gym & Fitness'],
      ]),
      makeGroup('entertainment', 'Entertainment', 'expense', [
        ['hobbies', 'Hobbies'],
        ['musicSubs', 'Music & TV Subscriptions'],
        ['techSubs', 'Technology Subscriptions'],
      ]),
      makeGroup('savings', 'Savings & Investments', 'savings', [
        ['retirement', 'Retirement Account'],
        ['investment', 'Investment Account'],
        ['emergency', 'Emergency Fund'],
      ]),
    ],
    annualBills: [],
  };
}

/**
 * Davo's real plan, carried over verbatim from the standalone tracker
 * (its DEFAULT_DATA), so the first month here starts where that app
 * left off instead of asking him to retype thirty numbers. Zero rows
 * are kept too — they're his placeholder vocabulary. Actuals start
 * blank; the plan is the part worth migrating.
 */
function davoSnapshot(): BudgetSnapshot {
  return {
    groups: [
      makeGroup('income', 'Income', 'income', [
        ['primarySalary', 'Primary Salary', 9300],
        ['extraIncome', 'Extra Income', 1500],
      ]),
      makeGroup('housing', 'Housing', 'expense', [
        ['rent', 'Rent', 4800],
        ['mobilePhone', 'Mobile Phone', 125],
        ['electricity', 'Electricity', 100],
        ['gas', 'Gas'],
        ['water', 'Water & Sewer'],
        ['internetTv', 'Internet & TV', 135],
        ['wasteRemoval', 'Waste Removal'],
        ['maintenance', 'Maintenance & Repairs'],
        ['supplies', 'Supplies'],
        ['housingOther', 'Other'],
      ]),
      makeGroup('transport', 'Transportation', 'expense', [
        ['vehiclePayment', 'Vehicle Payment'],
        ['publicTransport', 'Public Transport', 70],
        ['carPark', 'Car Park Rental', 250],
        ['fuel', 'Fuel', 50],
        ['carMaintenance', 'Maintenance', 50],
        ['transportOther', 'Other'],
      ]),
      makeGroup('food', 'Food & Dining', 'expense', [
        ['groceries', 'Groceries', 480],
        ['lunches', 'Lunches & Cafes', 350],
        ['diningOut', 'Dining Out', 150],
        ['foodOther', 'Other'],
      ]),
      makeGroup('insurance', 'Insurance', 'expense', [
        ['insHealth', 'Health'],
        ['insLife', 'Life'],
        ['insOther', 'Other'],
      ]),
      makeGroup('personalCare', 'Personal Care', 'expense', [
        ['medical', 'Medical'],
        ['hairdresser', 'Hairdressers', 50],
        ['clothing', 'Clothing & Accessories', 50],
        ['dryCleaning', 'Dry Cleaning'],
        ['gym', 'Gym & Fitness', 150],
        ['products', 'Products & Moisturisers', 50],
        ['careOther', 'Other'],
      ]),
      makeGroup('entertainment', 'Entertainment', 'expense', [
        ['hobbies', 'Hobbies & Self-Gifts', 30],
        ['vinyls', 'Vinyls'],
        ['movies', 'Movies', 30],
        ['concerts', 'Concerts'],
        ['sporting', 'Sporting Events'],
        ['theatre', 'Live Theatre'],
        ['musicSubs', 'Music & TV Subscriptions', 100],
        ['techSubs', 'Technology Subscriptions', 100],
        ['otherSubs', 'Other Subscriptions', 50],
        ['entOther', 'Other'],
      ]),
      makeGroup('children', 'Children', 'expense', [
        ['creche', 'Creche'],
        ['chSupplies', 'Supplies', 50],
        ['eduFund', 'Education Fund', 200],
        ['chOther', 'Other'],
      ]),
      makeGroup('france', 'Investment Property (France)', 'expense', [
        ['frElec', 'Electricity', 60],
        ['frInternet', 'Internet & TV', 40],
        ['frMobile', 'Mobile Phone', 15],
        ['frOther', 'Other'],
      ]),
      makeGroup('savings', 'Savings & Investments', 'savings', [
        ['retirement', 'Retirement Account', 1000],
        ['investment', 'Investment Account', 1000],
        ['emergency', 'Emergency Fund', 400],
        ['childInvest', 'Child Investment Accounts', 200],
        ['holiday', 'Holiday Fund', 100],
        ['savingsOther', 'Other'],
      ]),
      makeGroup('loans', 'Loans', 'expense', [
        ['personalLoan', 'Personal Loan'],
        ['studentLoan', 'Student Loan'],
        ['creditCard1', 'Credit Card 1'],
        ['creditCard2', 'Credit Card 2'],
        ['loanOther', 'Other'],
      ]),
      makeGroup('taxes', 'Taxes', 'expense', [
        ['taxFederal', 'Federal'],
        ['taxState', 'State'],
        ['taxLocal', 'Local'],
        ['taxOther', 'Other'],
      ]),
      makeGroup('gifts', 'Gifts & Donations', 'expense', [
        ['charity1', 'Charity 1'],
        ['charity2', 'Charity 2'],
        ['charity3', 'Charity 3'],
        ['giftsOther', 'Other'],
      ]),
    ],
    annualBills: [],
  };
}

const SEED_EMAIL = 'davo_simmons@hotmail.com';

/** The right first month for this user: Davo's migrated plan, or the
 *  zeroed starter vocabulary for anyone else. */
export function seedSnapshotFor(email: string | undefined): BudgetSnapshot {
  return email?.toLowerCase() === SEED_EMAIL ? davoSnapshot() : starterSnapshot();
}

// ── Balancer ────────────────────────────────────────────────────────
// "You're CHF X short — here are proportional cuts across flexible
// groups." Pure planning arithmetic over the snapshot; the sheet
// renders the plan and Apply rewrites the month via the normal commit.

/**
 * Largest-remainder apportionment: split a whole-unit `amount` across
 * `weights` proportionally so the parts are integers and sum exactly
 * to `amount`. Zero/negative weights get nothing.
 */
export function apportion(amount: number, weights: number[]): number[] {
  const safe = weights.map((w) => (w > 0 ? w : 0));
  const total = safe.reduce((s, w) => s + w, 0);
  if (total <= 0 || amount <= 0) return safe.map(() => 0);
  const raw = safe.map((w) => (amount * w) / total);
  const parts = raw.map(Math.floor);
  let left = amount - parts.reduce((s, v) => s + v, 0);
  const byRemainder = raw
    .map((r, i) => ({ i, frac: r - Math.floor(r) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (const { i } of byRemainder) {
    if (left <= 0) break;
    if (safe[i]! > 0) {
      parts[i]! += 1;
      left -= 1;
    }
  }
  return parts;
}

export interface BalancerCut {
  groupId: string;
  title: string;
  /** The group's own lines' planned total (÷12 provisions excluded —
   *  annual bills are yearly facts, not this month's dial). */
  before: number;
  after: number;
  cut: number;
}

export interface BalancerPlan {
  /** Whole-unit shortfall (planned committed − planned income, ceil). */
  shortfall: number;
  cuts: BalancerCut[];
  /** Sum of the flexible groups' cuttable lines. */
  flexibleBase: number;
  /** False when zeroing every flexible line still wouldn't balance. */
  feasible: boolean;
}

/**
 * The proportional-cut proposal for an overcommitted month, or null
 * when the plan already balances. Only expense groups not marked
 * `fixed` participate; savings are deliberately never touched (money
 * put away is spoken for — trimming it silently would defeat the
 * point of planning it).
 */
export function balancerPlan(snapshot: BudgetSnapshot): BalancerPlan | null {
  const totals = computeTotals(snapshot);
  const shortfall = Math.ceil(-totals.balancePlanned);
  if (shortfall <= 0) return null;
  const flex = snapshot.groups.filter((g) => g.kind === 'expense' && !g.fixed);
  const bases = flex.map((g) => g.items.reduce((s, i) => s + i.planned, 0));
  const flexibleBase = bases.reduce((s, b) => s + b, 0);
  const target = Math.min(shortfall, Math.floor(flexibleBase));
  const parts = apportion(target, bases);
  const cuts = flex
    .map((g, i) => ({
      groupId: g.id,
      title: g.title,
      before: bases[i]!,
      after: bases[i]! - parts[i]!,
      cut: parts[i]!,
    }))
    .filter((c) => c.before > 0);
  return { shortfall, cuts, flexibleBase, feasible: flexibleBase >= shortfall };
}

/**
 * Apply a balancer plan: each cut group's lines shrink proportionally
 * (whole units, largest-remainder), never below zero. Fixed groups,
 * savings, income, and annual bills are untouched by construction.
 */
export function applyBalancerCuts(s: BudgetSnapshot, plan: BalancerPlan): BudgetSnapshot {
  let next = s;
  for (const c of plan.cuts) {
    if (c.cut <= 0) continue;
    const group = next.groups.find((g) => g.id === c.groupId);
    if (!group) continue;
    const itemCuts = apportion(
      c.cut,
      group.items.map((i) => i.planned),
    );
    next = {
      ...next,
      groups: next.groups.map((g) =>
        g.id !== c.groupId
          ? g
          : {
              ...g,
              items: g.items.map((item, idx) => ({
                ...item,
                planned: Math.max(0, item.planned - itemCuts[idx]!),
              })),
            },
      ),
    };
  }
  return next;
}

// ── Trends ──────────────────────────────────────────────────────────

export interface TrendPoint {
  month: string;
  income: number;
  committed: number;
  /** 0–1, as computeTotals reports it. */
  savingsRate: number;
}

/**
 * Planned totals for the last `count` saved months up to and including
 * `throughMonth`, oldest first — the Trends sparklines. Empty (rather
 * than a single point) below two months: one dot isn't a trend.
 */
export function trendSeries(
  months: BudgetMonthDTO[],
  throughMonth: string,
  count = 6,
): TrendPoint[] {
  const eligible = months
    .filter((m) => m.month <= throughMonth)
    .sort((a, b) => a.month.localeCompare(b.month))
    .slice(-count);
  if (eligible.length < 2) return [];
  return eligible.map((m) => {
    const t = computeTotals(m.data);
    return {
      month: m.month,
      income: t.incomePlanned,
      committed: t.committedPlanned,
      savingsRate: t.savingsRate,
    };
  });
}
